import { Component, inject, input, computed, ChangeDetectionStrategy } from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import { TaskDataService } from "../../services/task-data.service";
import { IAudiLog } from "./task-history.models";
import { ITaskResponse } from "../../../modules/tasks/model/types";
import { switchMap, of, concat, map } from "rxjs";

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-task-history',
  templateUrl: './task-history.component.html',
  styleUrls: ['./task-history.component.css']
})
export class TaskHistoryComponent {
  readonly #taskDataService = inject(TaskDataService);

  task = input<ITaskResponse>();
  
  readonly #taskId = computed(() => this.task().parentId ?? this.task().id);

  dataSource = toSignal(
    toObservable(this.#taskId).pipe(
      switchMap(taskId => 
        taskId 
          ? concat(of([]), this.#taskDataService.getTaskHistory(taskId))
          : of([])
      ),
      map(logs => logs.map(log => ({
        ...log,
        column: this.formatColumnName(log.column),      
      })))
    ),
    { initialValue: [] as IAudiLog[] }
  );

  formatColumnName(value: string): string {
    if (!value) return value;
    return value
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, char => char.toUpperCase());
  }

  detectContentType(value: string): 'json' | 'html' | 'text' {
    if (!value || typeof value !== 'string') {
      return 'text';
    }

    const trimmed = value.trim();

    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        JSON.parse(trimmed);
        return 'json';
      } catch {
      }
    }

    const htmlPattern = /<\/?[a-z][\s\S]*>/i;
    if (htmlPattern.test(trimmed)) {
      return 'html';
    }

    return 'text';
  }

  formatValue(value: string): string {
    const type = this.detectContentType(value);

    switch (type) {
      case 'json':
        try {
          const parsed = JSON.parse(value);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return value;
        }
      case 'html':       
        return value;
      default:
        return value;
    }
  }

  isJson(value: string): boolean {
    return this.detectContentType(value) === 'json';
  }

  isHtml(value: string): boolean {
    return this.detectContentType(value) === 'html';
  }
}